import {
	DetailField,
	DetailSection,
	DetailFieldsGrid
} from '@/components/ui/details-panel';
import { EntityDetailField } from '@/components/ui/entity-detail-field';
import { Badge } from '@/components/ui/badge';
import { StatusBadgeDisplay } from '@/components/ui/status-badge-display';
import type { ParkingSessionDetail } from './types';
import { useTranslation } from 'react-i18next';
import {
	formatDateTime,
	formatCurrency,
	formatDuration
} from './utils';

interface ParkingSessionDetailsProps {
	session: ParkingSessionDetail;
}

export const ParkingSessionDetails = ({
	session
}: ParkingSessionDetailsProps) => {
	const { t } = useTranslation('common');
	const { t: tPS } = useTranslation('parkingSessions');

	return (
		<div className="space-y-6 text-sm">
			{/* Customer & Contract */}
			{(session.customerId || session.contractId) && (
				<DetailSection title={t('sections.customer_contract')} noBorderTop>
					<DetailFieldsGrid>
						{session.customerId && (
							<>
								<EntityDetailField
									label={tPS('details.customer')}
									value={session.customerName}
									entityId={session.customerId}
									entityType="customers"
								/>
								<DetailField
									label={tPS('details.email')}
									value={session.customerEmail}
								/>
							</>
						)}
						{session.contractId && (
							<>
								<EntityDetailField
									label={tPS('details.contract')}
									value={session.contractCustomerName || 'Contract'}
									entityId={session.contractId}
									entityType="contracts"
								/>
								<DetailField
									label={tPS('details.contractAmount')}
									value={formatCurrency(
										session.contractAmount
									)}
								/>
								<DetailField
									label={tPS('details.contractStatus')}
									value={
										<StatusBadgeDisplay
											isActive={Boolean(
												session.contractIsActive
											)}
											activeLabel={tPS('details.active')}
											inactiveLabel={tPS(
												'details.inactive'
											)}
										/>
									}
								/>
							</>
						)}
					</DetailFieldsGrid>
				</DetailSection>
			)}

			{/* Timing Information */}
			<DetailSection title={t('sections.timing')} noBorderTop={!session.customerId && !session.contractId}>
				<DetailFieldsGrid>
					<DetailField
						label={tPS('details.entry')}
						value={formatDateTime(session.entryDateTime)}
					/>
					<DetailField
						label={tPS('details.exit')}
						value={formatDateTime(session.exitDateTime)}
					/>
					<DetailField
						label={tPS('details.paymentTime')}
						value={formatDateTime(session.paymentDateTime)}
					/>
					<DetailField
						label={tPS('details.duration')}
						value={formatDuration(session.durationMinutes)}
					/>
				</DetailFieldsGrid>
			</DetailSection>

			{/* Vehicle Information */}
			<DetailSection title={t('sections.vehicle')}>
				<DetailFieldsGrid>
					{session.vehicleId ? (
						<EntityDetailField
							label={tPS('details.plateNumber')}
							value={session.vehiclePlateNumber}
							entityId={session.vehicleId}
							entityType="vehicles"
						/>
					) : (
						<DetailField
							label={tPS('details.plateNumber')}
							value={session.vehiclePlateNumber}
						/>
					)}
					<DetailField
						label={tPS('details.vehicleType')}
						value={session.vehicleTypeName}
					/>
					{session.vehicleMake && (
						<DetailField
							label={tPS('details.make')}
							value={session.vehicleMake}
						/>
					)}
					{session.vehicleModel && (
						<DetailField
							label={tPS('details.model')}
							value={session.vehicleModel}
						/>
					)}
					{session.vehicleColor && (
						<DetailField
							label={tPS('details.color')}
							value={session.vehicleColor}
						/>
					)}
				</DetailFieldsGrid>
			</DetailSection>

			{/* Parking Location - only show if we have parking position data */}
			{session.parkingPositionCode && (
				<DetailSection title={t('sections.parking_location')}>
					<DetailFieldsGrid>
						<DetailField
							label={tPS('details.position')}
							value={session.parkingPositionCode}
						/>
					</DetailFieldsGrid>
				</DetailSection>
			)}

			{/* Payment Information */}
			<DetailSection title={t('sections.payment')}>
				<DetailFieldsGrid>
					<DetailField
						label={tPS('details.amount')}
						value={formatCurrency(session.paymentAmount)}
					/>
					<DetailField
						label={tPS('details.method')}
						value={session.paymentMethod}
					/>
					{session.cashierName && (
						<DetailField
							label={tPS('details.cashier')}
							value={session.cashierName}
						/>
					)}
				</DetailFieldsGrid>
			</DetailSection>

			{/* Services */}
			{session.services && session.services.length > 0 && (
				<DetailSection title={t('sections.additional_services')}>
					<div className="space-y-2">
						{session.services.map(service => (
							<div
								key={service.parkingSessionServiceId}
								className="flex items-center justify-between p-2 text-xs border rounded-md border-border"
							>
								<span className="font-medium">
									{service.serviceName}
								</span>
								<span className="text-muted-foreground">
									{formatCurrency(service.priceCharged)}
								</span>
							</div>
						))}
					</div>
				</DetailSection>
			)}

			{/* Voucher Usage */}
			{session.voucherUsages && session.voucherUsages.length > 0 && (
				<DetailSection title={t('sections.vouchers_applied')}>
					<div className="space-y-2">
						{session.voucherUsages.map(voucher => (
							<div
								key={voucher.voucherParkingSessionId}
								className="p-2 border rounded-md border-border"
							>
								<div className="flex items-center justify-between">
									<div className="text-xs">
										<p className="font-semibold text-foreground">
											{voucher.voucherCode}
										</p>
										<p className="text-muted-foreground">
											{voucher.voucherName}
										</p>
										{voucher.discountTypeName && (
											<p className="text-muted-foreground">
												{tPS('details.voucherType')}:{' '}
												{voucher.discountTypeName}
											</p>
										)}
									</div>
									{voucher.amountApplied && (
										<span className="text-xs font-semibold text-green-600 dark:text-green-400">
											-
											{formatCurrency(
												voucher.amountApplied
											)}
										</span>
									)}
								</div>
							</div>
						))}
					</div>
				</DetailSection>
			)}

			{/* Prepaid Card Transactions */}
			{session.prepaidCardTransactions &&
				session.prepaidCardTransactions.length > 0 && (
					<DetailSection
						title={t('sections.prepaid_card_transactions')}
					>
						<div className="space-y-2">
							{session.prepaidCardTransactions.map(
								transaction => (
									<div
										key={
											transaction.prepaidCardTransactionId
										}
										className="flex items-center justify-between p-2 text-xs border rounded-md border-border"
									>
										<div>
											<p className="font-medium">
												{transaction.cardNumber}
											</p>
											<p className="text-muted-foreground">
												{
													transaction.transactionTypeName
												}
											</p>
										</div>
										<span className="font-semibold">
											{formatCurrency(transaction.amount)}
										</span>
									</div>
								)
							)}
						</div>
					</DetailSection>
				)}

			{/* Reservations */}
			{session.reservations && session.reservations.length > 0 && (
				<DetailSection title={t('sections.related_reservations')}>
					<div className="space-y-2">
						{session.reservations.map(reservation => (
							<div
								key={reservation.reservationId}
								className="p-2 text-xs border rounded-md border-border"
							>
								<div className="flex items-center justify-between">
									<div>
										<p className="font-medium">
											{tPS('details.reserved')}:{' '}
											{formatDateTime(
												reservation.reservationDateTime
											)}
										</p>
										{reservation.arrivalDateTime && (
											<p className="text-muted-foreground">
												{tPS('details.arrived')}:{' '}
												{formatDateTime(
													reservation.arrivalDateTime
												)}
											</p>
										)}
									</div>
									{reservation.statusName && (
										<Badge variant="outline">
											{reservation.statusName}
										</Badge>
									)}
								</div>
							</div>
						))}
					</div>
				</DetailSection>
			)}

			{/* AADE Invoices */}
			{session.aadeInvoices && session.aadeInvoices.length > 0 && (
				<DetailSection title={t('sections.tax_invoices')}>
					<div className="space-y-2">
						{session.aadeInvoices.map(invoice => (
							<div
								key={invoice.aadeInvoiceId}
								className="p-2 text-xs border rounded-md border-border"
							>
								<div className="grid gap-1">
									{invoice.invoiceUid && (
										<div className="flex justify-between">
											<span className="text-muted-foreground">
												{tPS('details.uid')}:
											</span>
											<span className="font-mono">
												{invoice.invoiceUid}
											</span>
										</div>
									)}
									{invoice.invoiceMark && (
										<div className="flex justify-between">
											<span className="text-muted-foreground">
												{tPS('details.mark')}:
											</span>
											<span className="font-mono">
												{invoice.invoiceMark}
											</span>
										</div>
									)}
									{invoice.series && (
										<div className="flex justify-between">
											<span className="text-muted-foreground">
												{tPS('details.series')}:
											</span>
											<span>{invoice.series}</span>
										</div>
									)}
								</div>
							</div>
						))}
					</div>
				</DetailSection>
			)}
		</div>
	);
};
